// 通用编辑控件库：受控表单控件，复用 editor.css 的 .input/.select/.textarea/.editor-section/.theme-dot。
// 各场景编辑器（scenes/editors.tsx）经 useSceneData().update 的 Immer draft 改 data；本库只管 UI。
import type { CSSProperties, ReactNode } from 'react'
import { useEditorStore } from '../store/editor'

/** 分区（带 label / 可选 hint）。等价原 editors.tsx 内联的 Section。 */
export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div className="editor-section">
      <label>{label}</label>
      {children}
      {hint && <div className="editor-hint">{hint}</div>}
    </div>
  )
}

interface TextProps {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  style?: CSSProperties
  mono?: boolean
}
export function TextInput({ value, onChange, placeholder, style, mono }: TextProps) {
  return (
    <input
      className="input"
      value={value ?? ''}
      placeholder={placeholder}
      style={{ ...style, fontFamily: mono ? 'var(--font-mono)' : undefined }}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

interface NumProps {
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  step?: number
  placeholder?: string
  style?: CSSProperties
}
export function NumberInput({ value, onChange, min, max, step = 1, placeholder, style }: NumProps) {
  return (
    <input
      className="input"
      type="number"
      value={Number.isFinite(value) ? value : 0}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      style={style}
      onChange={(e) => {
        const n = parseFloat(e.target.value)
        onChange(Number.isFinite(n) ? n : 0)
      }}
    />
  )
}

export function TextArea({
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  rows?: number
  placeholder?: string
}) {
  return (
    <textarea
      className="textarea"
      rows={rows}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

export function SelectInput<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: readonly T[]
}) {
  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value as T)}>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  )
}

// 各场景常用色板（柱状/排名/新闻/统计通用）
const DEFAULT_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#fafafa']

/** 颜色选择：预设色圆点（复用 .theme-dot）+ 原生 color input 自定义。 */
export function ColorField({
  value,
  onChange,
  presets = DEFAULT_COLORS,
}: {
  value: string
  onChange: (v: string) => void
  presets?: string[]
}) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#6366f1'
  return (
    <div className="color-field">
      <div className="theme-dots">
        {presets.map((c) => (
          <div
            key={c}
            className={`theme-dot${(value || '').toLowerCase() === c.toLowerCase() ? ' active' : ''}`}
            style={{ background: c }}
            onClick={() => onChange(c)}
          />
        ))}
      </div>
      <label className="color-custom">
        <input type="color" value={safe} onChange={(e) => onChange(e.target.value)} aria-label="自定义颜色" />
        <span className="color-custom-text">{value || '自定义'}</span>
      </label>
    </div>
  )
}

/** 经纬度编辑（map-trajectory cities）。lng/lng 两个 number。 */
export function CoordField({
  lng,
  lat,
  onLng,
  onLat,
}: {
  lng: number
  lat: number
  onLng: (v: number) => void
  onLat: (v: number) => void
}) {
  return (
    <div className="coord-field">
      <NumberInput value={lng} onChange={onLng} step={0.0001} placeholder="lng" />
      <NumberInput value={lat} onChange={onLat} step={0.0001} placeholder="lat" />
    </div>
  )
}

/** 列表「+ 添加」按钮。 */
export function Repeater({ addLabel = 'Add', onAdd }: { addLabel?: string; onAdd: () => void }) {
  return (
    <button type="button" className="btn btn-outline btn-sm repeater-add" onClick={onAdd}>
      + {addLabel}
    </button>
  )
}

/** 列表项删除按钮（小圆形 ✕，悬浮变红）。 */
export function RemoveButton({ onRemove, label = 'Remove' }: { onRemove: () => void; label?: string }) {
  return (
    <button type="button" className="ei-remove" onClick={onRemove} aria-label={label} title={label}>
      ✕
    </button>
  )
}

/** 时长编辑（全局，放 EditorPanel 顶部）：调 store.setDuration，预览时轴立即同步。 */
export function DurationField() {
  const duration = useEditorStore((s) => s.duration)
  const setDuration = useEditorStore((s) => s.setDuration)
  return (
    <Field label={`${duration}s`}>
      <div className="duration-field">
        <input
          type="range"
          className="play-scrub"
          min={2}
          max={30}
          step={1}
          value={Math.min(Math.max(duration, 2), 30)}
          onChange={(e) => setDuration(+e.target.value)}
        />
        <NumberInput value={duration} onChange={(v) => setDuration(Math.round(v))} min={2} max={60} style={{ width: 80 }} />
      </div>
    </Field>
  )
}
